import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Switch,
  Select,
  Input,
  InputNumber,
  message,
  Space,
} from "antd";
import { CopyOutlined, CalendarOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { updateProperty } from "../../lib/api/propertiesApi";

const { Option } = Select;

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

const LUXON_DAYS = [
  { v: 1, label: "Monday" },
  { v: 2, label: "Tuesday" },
  { v: 3, label: "Wednesday" },
  { v: 4, label: "Thursday" },
  { v: 5, label: "Friday" },
  { v: 6, label: "Saturday" },
  { v: 7, label: "Sunday" },
];

const SlotGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const WindowRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

function defaultWindows() {
  return [{ dayOfWeek: 2, start: "16:00", end: "18:00" }];
}

function normalizeDraft(p) {
  const c = p.showing_config || {};
  return {
    enabled: !!c.enabled,
    timezone: String(c.timezone || "America/New_York"),
    slotDurationMinutes: Number(c.slotDurationMinutes) || 30,
    horizonDays: Number(c.horizonDays) || 14,
    windows:
      Array.isArray(c.windows) && c.windows.length
        ? c.windows
        : defaultWindows(),
    customSlug: "",
  };
}

const LeasingShowingSettings = ({ properties, userUid, isLoading = false }) => {
  const queryClient = useQueryClient();
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (!properties?.length) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const p of properties) {
        if (!next[p.id]) next[p.id] = normalizeDraft(p);
      }
      return next;
    });
  }, [properties]);

  const syncDraftFromServer = (propertyId) => {
    const p = properties.find((x) => x.id === propertyId);
    if (!p) return;
    setDrafts((prev) => ({
      ...prev,
      [propertyId]: normalizeDraft(p),
    }));
  };

  const setField = (propertyId, patch) => {
    setDrafts((prev) => {
      const p = properties.find((x) => x.id === propertyId);
      const base = prev[propertyId] || normalizeDraft(p || {});
      return { ...prev, [propertyId]: { ...base, ...patch } };
    });
  };

  const copyLink = (slug) => {
    if (!slug) {
      message.warning("Save with scheduling on to get a link.");
      return;
    }
    const url = `${window.location.origin}/p/${slug}/schedule`;
    navigator.clipboard.writeText(url).then(
      () => message.success("Copied Zillow showing link"),
      () => message.error("Could not copy")
    );
  };

  const save = async (p) => {
    const d = drafts[p.id] || normalizeDraft(p);
    setSavingId(p.id);
    try {
      const windows = (d.windows || []).map((w) => ({
        dayOfWeek: Number(w.dayOfWeek),
        start: String(w.start || "").trim(),
        end: String(w.end || "").trim(),
      }));
      const payload = {
        showing_config: {
          enabled: d.enabled,
          timezone: d.timezone,
          slotDurationMinutes: d.slotDurationMinutes,
          horizonDays: d.horizonDays,
          windows,
        },
      };
      if (d.customSlug && d.customSlug.trim()) {
        payload.showing_link_slug = d.customSlug.trim().toLowerCase();
      }
      await updateProperty(p.id, payload);
      message.success("Leasing settings saved");
      queryClient.invalidateQueries({ queryKey: ["properties", userUid] });
      setField(p.id, { customSlug: "" });
    } catch (e) {
      message.error(e?.message || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          padding: "0 1.5rem 1.5rem",
          color: "var(--color-text-light)",
          fontSize: "0.95rem",
        }}
      >
        Loading leasing links...
      </div>
    );
  }

  if (!properties?.length) {
    return (
      <div
        style={{
          padding: "0 1.5rem 1.5rem",
          color: "var(--color-text-light)",
          fontSize: "0.95rem",
        }}
      >
        No properties found. Add a property in your account to enable public
        showing links.
      </div>
    );
  }

  return (
    <div style={{ padding: "0 1.5rem 1.5rem" }}>
      <div
        style={{
          fontSize: "0.9rem",
          color: "var(--color-text-light)",
          marginBottom: "1rem",
        }}
      >
        Share a public link in Zillow (or anywhere) so prospects can request a
        showing without a login. Times use your selected timezone.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {properties.map((p) => {
          const d = drafts[p.id] || normalizeDraft(p);
          const slug = p.showing_link_slug;
          return (
            <div
              key={p.id}
              style={{
                border: "1px solid var(--color-border-light)",
                borderRadius: 12,
                padding: "1rem",
                background: "var(--color-bg-secondary)",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CalendarOutlined style={{ color: "var(--color-primary)" }} />
                {p.address || p.id}
              </div>
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span style={{ color: "var(--color-text-light)" }}>
                    Public scheduling
                  </span>
                  <Switch
                    checked={d.enabled}
                    onChange={(checked) => setField(p.id, { enabled: checked })}
                  />
                  <Button
                    type="default"
                    icon={<CopyOutlined />}
                    onClick={() => copyLink(slug)}
                    disabled={!slug}
                  >
                    Copy Zillow showing link
                  </Button>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "var(--color-text-light)",
                      fontSize: "0.875rem",
                    }}
                  >
                    Custom link slug (optional)
                  </label>
                  <Input
                    placeholder="Leave blank to auto-generate on save"
                    value={d.customSlug}
                    onChange={(e) =>
                      setField(p.id, { customSlug: e.target.value })
                    }
                    addonBefore="/p/"
                    addonAfter="/schedule"
                  />
                  {slug && (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "var(--color-text-muted)",
                        marginTop: 6,
                      }}
                    >
                      Current: {slug}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "var(--color-text-light)",
                      fontSize: "0.875rem",
                    }}
                  >
                    Timezone
                  </label>
                  <Select
                    style={{ width: "100%", maxWidth: 320 }}
                    value={d.timezone}
                    onChange={(tz) => setField(p.id, { timezone: tz })}
                    options={US_TIMEZONES}
                  />
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        color: "var(--color-text-light)",
                        fontSize: "0.875rem",
                      }}
                    >
                      Slot length (minutes)
                    </label>
                    <Select
                      style={{ width: 120 }}
                      value={d.slotDurationMinutes}
                      onChange={(v) =>
                        setField(p.id, { slotDurationMinutes: v })
                      }
                    >
                      {[15, 30, 45, 60].map((m) => (
                        <Option key={m} value={m}>
                          {m}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        color: "var(--color-text-light)",
                        fontSize: "0.875rem",
                      }}
                    >
                      Days ahead
                    </label>
                    <InputNumber
                      min={1}
                      max={60}
                      value={d.horizonDays}
                      onChange={(v) => setField(p.id, { horizonDays: v ?? 14 })}
                    />
                  </div>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "var(--color-text-light)",
                      fontSize: "0.875rem",
                    }}
                  >
                    Showing windows
                  </label>
                  <SlotGrid>
                    {(d.windows || []).map((w, idx) => (
                      <WindowRow key={idx}>
                        <Select
                          style={{ width: 130 }}
                          value={w.dayOfWeek}
                          onChange={(v) => {
                            const next = [...(d.windows || [])];
                            next[idx] = { ...next[idx], dayOfWeek: v };
                            setField(p.id, { windows: next });
                          }}
                        >
                          {LUXON_DAYS.map((day) => (
                            <Option key={day.v} value={day.v}>
                              {day.label}
                            </Option>
                          ))}
                        </Select>
                        <Input
                          style={{ width: 88 }}
                          placeholder="16:00"
                          value={w.start}
                          onChange={(e) => {
                            const next = [...(d.windows || [])];
                            next[idx] = {
                              ...next[idx],
                              start: e.target.value,
                            };
                            setField(p.id, { windows: next });
                          }}
                        />
                        <span style={{ color: "var(--color-text-muted)" }}>
                          to
                        </span>
                        <Input
                          style={{ width: 88 }}
                          placeholder="18:00"
                          value={w.end}
                          onChange={(e) => {
                            const next = [...(d.windows || [])];
                            next[idx] = { ...next[idx], end: e.target.value };
                            setField(p.id, { windows: next });
                          }}
                        />
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => {
                            const next = (d.windows || []).filter(
                              (_, i) => i !== idx
                            );
                            setField(p.id, {
                              windows: next.length ? next : defaultWindows(),
                            });
                          }}
                        >
                          Remove
                        </Button>
                      </WindowRow>
                    ))}
                    <Button
                      type="dashed"
                      size="small"
                      onClick={() =>
                        setField(p.id, {
                          windows: [
                            ...(d.windows || []),
                            { dayOfWeek: 3, start: "12:00", end: "14:00" },
                          ],
                        })
                      }
                    >
                      Add window
                    </Button>
                  </SlotGrid>
                </div>
                <Space>
                  <Button
                    type="primary"
                    loading={savingId === p.id}
                    onClick={() => save(p)}
                  >
                    Save leasing settings
                  </Button>
                  <Button
                    type="default"
                    onClick={() => syncDraftFromServer(p.id)}
                  >
                    Reset
                  </Button>
                </Space>
              </Space>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeasingShowingSettings;
